import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

const BUCKET = "chat-attachments";

export interface SendMessageArgs {
  roomId: string;
  senderId: string;
  body?: string;
  file?: File | null;
  /** VIB-3025: nevnte brukere (teksten har «@Fullt Navn»). Varsel sendes av DB-trigger. */
  mentionedUserIds?: string[];
}

/** Find an existing direct-chat between two users, or create one. */
async function findOrCreateDirectChat(userA: string, userB: string, createdBy: string): Promise<string> {
  // Finn rom der begge er medlemmer
  const { data: aRooms, error: aErr } = await supabase
    .from("chat_members")
    .select("room_id, chat_rooms!inner(type)")
    .eq("user_id", userA);
  if (aErr) throw aErr;

  type JoinedMember = { room_id: string; chat_rooms: { type: string } | null };
  const directRoomIds = ((aRooms ?? []) as unknown as JoinedMember[])
    .filter((r) => r.chat_rooms?.type === "direct")
    .map((r) => r.room_id);

  if (directRoomIds.length > 0) {
    const { data: bRooms, error: bErr } = await supabase
      .from("chat_members")
      .select("room_id")
      .eq("user_id", userB)
      .in("room_id", directRoomIds);
    if (bErr) throw bErr;

    // Sørg for at rommet KUN har disse to (ikke en gammel gruppe feilmarkert)
    const match = (bRooms ?? [])[0]?.room_id;
    if (match) {
      const { count, error: cErr } = await supabase
        .from("chat_members")
        .select("id", { count: "exact", head: true })
        .eq("room_id", match);
      if (cErr) throw cErr;
      if (count === 2) return match;
    }
  }

  // Opprett nytt
  const { data: newRoom, error: roomErr } = await supabase
    .from("chat_rooms")
    .insert({ type: "direct", created_by: createdBy })
    .select("id")
    .single();
  if (roomErr) throw roomErr;

  const { error: memErr } = await supabase
    .from("chat_members")
    .insert([
      { room_id: newRoom.id, user_id: userA },
      { room_id: newRoom.id, user_id: userB },
    ]);
  if (memErr) throw memErr;

  return newRoom.id;
}

async function uploadAttachment(file: File, roomId: string, senderId: string) {
  const ext = file.name.split(".").pop() ?? "bin";
  const fileName = `${roomId}/${senderId}-${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(fileName, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (upErr) throw upErr;
  const url = supabase.storage.from(BUCKET).getPublicUrl(fileName).data.publicUrl;
  return { url, name: file.name, type: file.type, size: file.size };
}

export function useChatActions(activeUserId: string | null | undefined) {
  const qc = useQueryClient();

  const sendMessage = useMutation({
    mutationFn: async (args: SendMessageArgs) => {
      const body = args.body?.trim() || null;
      if (!body && !args.file) throw new Error("Tom melding");

      let attachment: { url: string; name: string; type: string; size: number } | null = null;
      if (args.file) {
        attachment = await uploadAttachment(args.file, args.roomId, args.senderId);
      }

      const { error } = await supabase.from("chat_messages").insert({
        room_id: args.roomId,
        sender_id: args.senderId,
        body,
        attachment_url: attachment?.url ?? null,
        attachment_name: attachment?.name ?? null,
        attachment_type: attachment?.type ?? null,
        attachment_size_bytes: attachment?.size ?? null,
        mentioned_user_ids: args.mentionedUserIds ?? [],
      });
      if (error) throw error;
    },
    onSuccess: async (_, vars) => {
      await invalidateFor(qc, "chat_messages", {
        roomId: vars.roomId,
        userId: vars.senderId,
      });
    },
    onError: (e: Error) => {
      console.error("Feil ved sending av melding:", e);
      toast.error(`Kunne ikke sende melding${e?.message ? `: ${e.message}` : ""}`);
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (roomId: string) => {
      if (!activeUserId) return;
      const { error } = await supabase
        .from("chat_members")
        .update({ last_read_at: new Date().toISOString() })
        .eq("room_id", roomId)
        .eq("user_id", activeUserId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "chat_members", { userId: activeUserId });
    },
  });

  const createDirectChat = useMutation({
    mutationFn: async (otherUserId: string) => {
      if (!activeUserId) throw new Error("Ingen aktiv bruker");
      return findOrCreateDirectChat(activeUserId, otherUserId, activeUserId);
    },
    onSuccess: async () => {
      // Direct chat creation writes to both chat_rooms and chat_members.
      await invalidateFor(qc, "chat_rooms", { userId: activeUserId });
      await invalidateFor(qc, "chat_members", { userId: activeUserId });
    },
    onError: (e: Error) => {
      console.error("Feil ved opprettelse av direktechat:", e);
      toast.error("Kunne ikke opprette chat");
    },
  });

  const createGroupChat = useMutation({
    mutationFn: async ({ name, memberIds }: { name: string; memberIds: string[] }) => {
      if (!activeUserId) throw new Error("Ingen aktiv bruker");
      const trimmedName = name.trim();
      if (!trimmedName) throw new Error("Gruppen må ha et navn");

      const uniqueMembers = Array.from(new Set([activeUserId, ...memberIds]));
      if (uniqueMembers.length < 2) throw new Error("Velg minst én annen person");

      const { data: newRoom, error: roomErr } = await supabase
        .from("chat_rooms")
        .insert({ type: "group", name: trimmedName, created_by: activeUserId })
        .select("id")
        .single();
      if (roomErr) throw roomErr;

      const { error: memErr } = await supabase
        .from("chat_members")
        .insert(uniqueMembers.map((user_id) => ({ room_id: newRoom.id, user_id })));
      if (memErr) throw memErr;

      return newRoom.id as string;
    },
    onSuccess: async () => {
      // Group creation writes to both chat_rooms and chat_members.
      await invalidateFor(qc, "chat_rooms", { userId: activeUserId });
      await invalidateFor(qc, "chat_members", { userId: activeUserId });
    },
    onError: (e: Error) => {
      console.error("Feil ved opprettelse av gruppechat:", e);
      toast.error(e?.message ?? "Kunne ikke opprette gruppe");
    },
  });

  return { sendMessage, markAsRead, createDirectChat, createGroupChat };
}
